import React, { useEffect, useState } from 'react';
import { Loader2, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { atlasApi, AtlasUser, FeedbackCiclo, FeedbackPergunta } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  cicloId: string;
  avaliadoFunccodi: number;
  souEu: boolean;
  onConcluir: () => void;
}

const RELACOES = [
  { id: 'gestor', label: 'Sou gestor(a)' },
  { id: 'par', label: 'Sou par/colega' },
  { id: 'subordinado', label: 'Sou subordinado(a)' },
];

const Responder: React.FC<Props> = ({ user, cicloId, avaliadoFunccodi, souEu, onConcluir }) => {
  const [ciclo, setCiclo] = useState<FeedbackCiclo | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [tipoRelacao, setTipoRelacao] = useState<string>(souEu ? 'autoavaliacao' : 'par');
  const [valores, setValores] = useState<Record<number, any>>({});

  useEffect(() => {
    let cancel = false;
    atlasApi.getCiclo(user, cicloId)
      .then(r => { if (!cancel) setCiclo(r.ciclo); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, cicloId]);

  const setVal = (pid: number, v: any) => setValores(p => ({ ...p, [pid]: v }));

  const enviar = async () => {
    if (!ciclo) return;
    const faltando = ciclo.perguntas.some(p => valores[p.id] === undefined || valores[p.id] === '');
    if (faltando) { setErro('Responda todas as perguntas.'); return; }
    setSalvando(true);
    setErro(null);
    const respostas = ciclo.perguntas.map(p => ({ pergunta_id: p.id, valor: valores[p.id] }));
    try {
      await atlasApi.responderFeedback(user, { ciclo_id: cicloId, avaliado_funccodi: avaliadoFunccodi, tipo_relacao: tipoRelacao, respostas });
      onConcluir();
    } catch (e: any) {
      setErro(e.message || 'Erro ao enviar.');
      setSalvando(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>;
  if (erro && !ciclo) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;
  if (!ciclo) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h2 className="text-lg font-bold text-gray-900">{ciclo.titulo}</h2>
        <p className="text-sm text-gray-500">{souEu ? 'Autoavaliação' : `Avaliando colaborador #${avaliadoFunccodi}`}</p>
        {!souEu && (
          <div className="mt-3">
            <label className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block">Sua relação</label>
            <div className="flex gap-2 flex-wrap">
              {RELACOES.map(r => (
                <button key={r.id} onClick={() => setTipoRelacao(r.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${tipoRelacao === r.id ? 'bg-purple-100 text-purple-700' : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'}`}>{r.label}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {ciclo.perguntas.map((p: FeedbackPergunta) => (
        <div key={p.id} className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="text-sm font-medium text-gray-800 mb-1">{p.texto}</div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-3">{p.categoria}</div>
          {p.tipo === 'escala_1_5' && (
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setVal(p.id, n)} className={`w-10 h-10 rounded-lg text-sm font-bold border transition ${valores[p.id] === n ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'}`}>{n}</button>
              ))}
            </div>
          )}
          {p.tipo === 'sim_nao' && (
            <div className="flex gap-2">
              {['sim', 'nao'].map(v => (
                <button key={v} onClick={() => setVal(p.id, v)} className={`px-5 py-2 rounded-lg text-sm font-medium border transition ${valores[p.id] === v ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'}`}>{v === 'sim' ? 'Sim' : 'Não'}</button>
              ))}
            </div>
          )}
          {p.tipo === 'texto_livre' && (
            <textarea className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-purple-200" value={valores[p.id] || ''} onChange={e => setVal(p.id, e.target.value)} placeholder="Escreva seu feedback..." />
          )}
        </div>
      ))}

      {erro && <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm"><AlertCircle className="w-4 h-4" /> {erro}</div>}

      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Suas respostas são agregadas anonimamente no relatório.</span>
        <button onClick={enviar} disabled={salvando} className="ml-auto px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2">
          {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar avaliação
        </button>
      </div>
    </div>
  );
};

export default Responder;
